// EmptyState: nothing here, said so it teaches and offers the way on. Three shapes, by where it
// sits: `panel` stands in for a whole surface (nothing yet, or nothing matching the filters),
// `area` fills an empty region already inside a panel or table, `line` is the one sentence in a
// card's list. A first-run panel can carry EmptyMark, the one moment of charm here.
// docs/operations/design-standard.md, "Feedback".

import React, { useEffect, useState } from 'react';
import { IconSpark } from './icons';
import { reducedMotion } from './motion';

export type EmptyStateVariant = 'panel' | 'area' | 'line';

export interface EmptyStateProps {
  variant?: EmptyStateVariant;
  /** A short heading for a first run ("No plans yet"). The sentence then becomes its lede. */
  title?: React.ReactNode;
  /** The title's element, so it can be the page's or the card's heading. Same look at every level. */
  titleAs?: 'p' | 'h1' | 'h2' | 'h3';
  /** The sentence: what would be here, or what hid it ("Nothing in Shows matches ..."). */
  children?: React.ReactNode;
  /**
   * The way on, as kit Buttons: the first one (filled New ...) on a first run, a tonal Clear
   * where a filter hid everything, a quiet key on a line.
   */
  action?: React.ReactNode;
  /** An EmptyMark above the words, for a first run. Panel only. */
  mark?: React.ReactNode;
  /** `line` in a tight slot (a comments box): meta size instead of body. */
  dense?: boolean;
  className?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  variant = 'panel', title, titleAs: Title = 'p', children, action, mark, dense, className = '',
}) => {
  if (variant === 'line') {
    return (
      <div className={`flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-on-surface-variant ${dense ? 'text-xs leading-4' : 'text-sm leading-5'} ${className}`}>
        <span className="min-w-0">
          {title && <span className="font-medium text-on-surface">{title} </span>}
          {children}
        </span>
        {action && <span className="flex flex-none items-center gap-2">{action}</span>}
      </div>
    );
  }

  const box = variant === 'panel' ? 'rounded-panel bg-surface-container-low px-5 py-10' : 'px-4 py-8';
  return (
    <div className={`flex min-w-0 flex-col items-center text-center ${box} ${className}`}>
      {variant === 'panel' && mark && <div className="mb-3 flex">{mark}</div>}
      {title && <Title className="m-0 text-balance text-base font-semibold leading-6 text-on-surface">{title}</Title>}
      {children && (
        <div className={title ? 'mt-1 max-w-md text-pretty text-sm leading-5 text-on-surface-variant' : 'max-w-prose text-pretty text-sm leading-5 text-on-surface'}>
          {children}
        </div>
      )}
      {action && <div className={`flex flex-wrap items-center justify-center gap-2 ${title ? 'mt-4' : 'mt-3'}`}>{action}</div>}
    </div>
  );
};

/**
 * The mark lights up once on arrival: it grows in, flares a little past full, then settles,
 * glowing in the accent. Under reduced motion it fades in at its size. `icon` replaces the
 * firefly glint with a feature's own mark.
 */
export const EmptyMark: React.FC<{ icon?: React.ReactNode }> = ({ icon }) => {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const id = requestAnimationFrame(() => setPhase(1));
    if (reducedMotion()) return () => cancelAnimationFrame(id);
    const timers = [window.setTimeout(() => setPhase(2), 520), window.setTimeout(() => setPhase(3), 900)];
    return () => { cancelAnimationFrame(id); timers.forEach(window.clearTimeout); };
  }, []);
  const look = phase === 0 ? 'scale-50 -rotate-12 opacity-0' : phase === 2 ? 'scale-125 rotate-0 opacity-100' : 'scale-100 rotate-0 opacity-100';
  return (
    <span
      aria-hidden="true"
      className={`inline-flex text-primary transition-[transform,opacity,filter] duration-spring ease-spring-bouncy motion-reduce:rotate-0 motion-reduce:scale-100 motion-reduce:transition-[opacity] ${look} ${phase >= 1 ? 'drop-shadow-glow' : ''}`}
    >
      {icon || <IconSpark className="h-12 w-12" />}
    </span>
  );
};

export default EmptyState;
